import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { Toaster, toast } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import Onboarding from '@/pages/Onboarding';
import EditProfile from '@/pages/EditProfile';
import Welcome from '@/pages/Welcome';
import AwaitingVouch from '@/pages/AwaitingVouch';
import Feed from '@/pages/Feed';
import Matches from '@/pages/Matches';
import Messages from '@/pages/Messages';
import Chat from '@/pages/Chat';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';
import EkJiv from '@/pages/EkJiv';
import Admin from '@/pages/Admin';
import BottomNav from '@/components/BottomNav';
import '@/App.css';

function MainLayout() {
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden">
        <Outlet />
      </div>
      <BottomNav />
    </div>
  );
}

function AppRoutes() {
  const { user, profile, loading } = useAuth();

  // Screenshot prevention: disable right-click on images, listen for visibility changes
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target?.tagName === 'IMG') {
        e.preventDefault();
        toast.error('Screenshots aren\'t allowed on Jiv. This protects everyone\'s privacy.');
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const count = parseInt(localStorage.getItem('jiv_screenshot_count') || '0', 10) + 1;
        localStorage.setItem('jiv_screenshot_count', count);
        
        if (count === 1) {
          toast.warning('Screenshots aren\'t allowed on Jiv. This protects everyone\'s privacy.');
        }
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary tracking-tight">Jiv</h1>
          <p className="text-muted-foreground mt-2 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/admin" element={<Admin />} />
      {!user ? (
        <Route path="*" element={<Login />} />
      ) : !profile ? (
        <>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </>
      ) : profile.admin_blocked ? (
        <Route path="*" element={<AccountRemovedScreen />} />
      ) : profile.report_flag ? (
        <Route path="*" element={<ReportFlagScreen />} />
      ) : !profile.selfie_verified ? (
        <>
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/edit-profile" element={<EditProfile />} />
          <Route path="*" element={<AwaitingVouch />} />
        </>
      ) : (
        <>
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/edit-profile" element={<EditProfile />} />
          <Route element={<MainLayout />}>
            <Route index element={<Feed />} />
            <Route path="/ek-jiv" element={<EkJiv />} />
            <Route path="/matches" element={<Matches />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
          </Route>
          <Route path="/messages/:matchId" element={<Chat />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
  );
}

function ReportFlagScreen() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-center bg-[#FAF8F5] px-4 text-center">
      <div className="mb-6">
        <div className="h-16 w-16 rounded-full bg-[#F7D7EA] flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">⚠️</span>
        </div>
      </div>
      <h1 className="text-2xl font-bold text-[#1A1814] mb-2">Profile Under Review</h1>
      <p className="text-[#7A7672] mb-6 max-w-sm">
        Your profile is currently under review. We'll get back to you shortly.
      </p>
      <p className="text-sm text-[#A8A5A0]">
        Questions? Contact us at <span className="font-medium text-[#1A1814]">hello@jivapp.in</span>
      </p>
      <button
        onClick={handleLogout}
        className="mt-8 px-6 py-2 text-sm text-[#7A7672] border border-[#D0CDC8] rounded-full hover:bg-[#F0EDE8] transition-colors"
      >
        Log out
      </button>
    </div>
  );
}

function AccountRemovedScreen() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-center bg-[#FAF8F5] px-4 text-center">
      <div className="mb-6">
        <div className="h-16 w-16 rounded-full bg-[#F7D7EA] flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">🚫</span>
        </div>
      </div>
      <h1 className="text-2xl font-bold text-[#1A1814] mb-2">Account Removed</h1>
      <p className="text-[#7A7672] mb-6 max-w-sm">
        Your account has been removed. Contact hello@jivapp.in
      </p>
      <button
        onClick={handleLogout}
        className="px-6 py-2 text-sm text-[#7A7672] border border-[#D0CDC8] rounded-full hover:bg-[#F0EDE8] transition-colors"
      >
        Log out
      </button>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="max-w-md mx-auto h-[100dvh] overflow-hidden relative bg-background md:border md:border-border">
          <AppRoutes />
          <Toaster position="top-center" />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
